import { DateInput } from "@msuite/ui/date-input";
import { FileUpload, type FileUploadController } from "@msuite/ui/file-upload";
import { Select, Textarea, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { INVOICE_TYPES } from "@msuite/utils/invoices";
import type { TranslationKey } from "@msuite/utils/locales";
import type { ProjectInvoiceSchema } from "@msuite/utils/schemas";
import { useFormContext } from "react-hook-form";
import { FileCardRemote } from "@/components/files";
import { InvoicesStateSelect } from "@/components/invoices";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { ProjectInvoiceFormProgressInvoiceNumber } from "./project-invoice-form-progress-invoice-number";

/** Props Interface */
interface ProjectInvoiceFormProps {
	controller: FileUploadController;
}

export function ProjectInvoiceForm({ controller }: ProjectInvoiceFormProps) {
	/** Context */
	const methods = useFormContext<ProjectInvoiceSchema>();

	/** Hooks */
	const mentionOptions = useMentionOptions();
	const { t } = useTranslation();

	/** Render */
	return (
		<div className="flex flex-col gap-6">
			<div className="gap-6 grid grid-cols-2">
				<DateInput
					control={methods.control}
					name="creationDate"
					label={t("invoices.creation_date")}
					isRequired
				/>
				<DateInput
					control={methods.control}
					name="calculatedTo"
					label={t("invoices.calculated_to")}
					isRequired={false}
				/>
			</div>
			<TextInput
				control={methods.control}
				name="number"
				label={t("invoices.number")}
				isRequired
				placeholder={t("placeholders.invoice_number")}
				autoFocus
				data-cy="project-invoice-number"
			/>
			<Select
				label={t("invoices.type")}
				control={methods.control}
				name="type"
				isRequired
				options={INVOICE_TYPES.map((type) => ({
					value: type,
					label: t(`invoices.${type.replaceAll("-", "_")}` as TranslationKey),
					item: null,
				}))}
				itemNotFoundLabel={t("general.not_found")}
				data-cy="project-invoice-type"
			/>
			<ProjectInvoiceFormProgressInvoiceNumber />
			<TextInput
				label={t("invoices.sum_net")}
				control={methods.control}
				name="sum"
				isRequired
				type="number"
				placeholder="0.00"
				step={0.01}
				data-cy="project-invoice-sum"
			/>
			<InvoicesStateSelect
				label={t("invoices.state")}
				defaultValue={methods.getValues("state")}
				onChange={(value) => {
					if (value === "all") return;
					methods.setValue("state", value);
				}}
				data-cy="invoice-form-state-select"
			/>
			<Textarea
				isRequired={false}
				control={methods.control}
				name="note"
				label={t("invoices.note")}
				placeholder={t("placeholders.add_note")}
				disableFormatting
				{...mentionOptions}
				data-cy="project-invoice-note"
			/>
			<FileUpload
				controller={controller}
				renderFile={(file) => (
					<FileCardRemote
						fileId={file.id}
						onInteraction={async () => {}}
					/>
				)}
			/>
		</div>
	);
}
